import { ContactDetail } from "@deligoo/shared"
import { Button, Icon } from "@deligoo/ui"
import { IconName } from "@deligoo/ui/src/icons/build"
import dayjs from "dayjs"
import { ReactNode, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"

import { useAppDispatch, useAppSelector } from "@/App"
import { Card } from "@/components/Card"
import { selectClients } from "@/store/clients"
import { selectDrivers } from "@/store/drivers"
import { setAboutPanel } from "@/store/ui"
import { Order } from "@/types"
import { fetchCreateOrder, formatPhoneNumber, formatPrice } from "@/utils"
import { handleStandardError } from "@/utils/errors"

import { useManualOrderFormContext } from "../AddManualOrderView"
import cls from "../AddManualOrderView.module.scss"

export const SummaryView = () => {
  const clients = useAppSelector(selectClients)
  const drivers = useAppSelector(selectDrivers)

  const { orderKind, formState, setPreviousStep } = useManualOrderFormContext()

  const [createdOrderId, setCreatedOrderId] = useState<Order["id"] | null>(null)

  const [isLoading, setLoading] = useState(false)

  const { t } = useTranslation()

  async function createManualOrder() {
    setLoading(true)

    const { response, json } = await fetchCreateOrder(formState.values)

    setLoading(false)

    if (response.ok && json.data) {
      setCreatedOrderId(json.data.id)
      return
    }

    handleStandardError(response, json)
  }

  if (createdOrderId) return <OrderCreated orderId={createdOrderId} />

  return (
    <Card className={cls.card}>
      <Card.Section>
        <h3>Podsumowanie</h3>
      </Card.Section>

      <Card.Section className={cls.summaries}>
        <SummaryBox>
          <SummaryHeader iconName="tasks">Typ zlecenia</SummaryHeader>

          <SummaryInfo>
            <li>{t(`delivery_method.${orderKind}`)}</li>
          </SummaryInfo>
        </SummaryBox>

        <SummaryBox>
          <SummaryHeader iconName="restaurants">Dane odbioru/doręczenia</SummaryHeader>

          <SummaryInfo>
            <li>
              Odbiór:
              <dl className="mt-3">
                <div>
                  <dt>Klient</dt>
                  <dd>{clients.find((client) => client.id === formState.values.client_id)?.name}</dd>
                </div>

                {formState.values.pickup_attributes?.phone && (
                  <div>
                    <dt>Telefon</dt>
                    <dd>{formatPhoneNumber(formState.values.pickup_attributes?.phone)}</dd>
                  </div>
                )}

                {formState.values.pickup_attributes && (
                  <div>
                    <dt>Adres</dt>
                    <dd>{humanizeAddress(formState.values.pickup_attributes)}</dd>
                  </div>
                )}

                {formState.values.pickup_at && (
                  <div>
                    <dt>Data odbioru</dt>
                    <dd>{dayjs(formState.values.pickup_at).format("DD.MM.YY HH:mm")}</dd>
                  </div>
                )}
              </dl>
            </li>

            {formState.values.delivery_attributes && (
              <li className="mt-5">
                Dostawa:
                <dl className="mt-3">
                  {formState.values.delivery_attributes?.company_name && (
                    <div>
                      <dt>Nazwa</dt>
                      <dd>{formState.values.delivery_attributes?.company_name}</dd>
                    </div>
                  )}

                  {formState.values.delivery_attributes?.phone && (
                    <div>
                      <dt>Telefon</dt>
                      <dd>{formatPhoneNumber(formState.values.delivery_attributes?.phone)}</dd>
                    </div>
                  )}

                  {formState.values.delivery_attributes && (
                    <div>
                      <dt>Adres</dt>
                      <dd>{humanizeAddress(formState.values.delivery_attributes)}</dd>
                    </div>
                  )}

                  {formState.values.delivery_at && (
                    <div>
                      <dt>Data dostawy</dt>
                      <dd>{dayjs(formState.values.delivery_at).format("DD.MM.YY HH:mm")}</dd>
                    </div>
                  )}
                </dl>
              </li>
            )}
          </SummaryInfo>
        </SummaryBox>

        <SummaryBox>
          <SummaryHeader iconName="rocket">Parametry</SummaryHeader>

          <SummaryInfo>
            {formState.values.proposed_driver_id && (
              <li>Kierowca: {drivers.find((driver) => driver.id === formState.values.proposed_driver_id)?.name}</li>
            )}
            <li>Transport: Rower / Samochód / Skuter</li>
            <li>Płatność: {t(`orders:payment_form.${formState.values.payment_form}`)}</li>
            <li>Kwota zamówienia: {formatPrice(formState.values.price_subunit, { subunit: true })} PLN</li>
            <li>Koszt dostawy: {formatPrice(formState.values.price_delivery_subunit, { subunit: true })} PLN</li>
          </SummaryInfo>
        </SummaryBox>

        <SummaryBox>
          <SummaryHeader iconName="puzzle">Pozostałe</SummaryHeader>

          <SummaryInfo>
            <li>
              Rodzaj zamówienia:
              <div className="mt-3">
                {Object.entries(formState.values.packages || {}).map(([type, amount]) => (
                  <div className="text-muted h200" key={type}>
                    {t(`orders:package_type.${type}`)}: {amount} szt.
                  </div>
                ))}
              </div>
            </li>

            {formState.values.order_comment_attributes?.description && (
              <li className="mt-5">
                Uwagi do zamówienia:
                <div className="text-muted h200 mt-3">{formState.values.order_comment_attributes?.description}</div>
              </li>
            )}
          </SummaryInfo>
        </SummaryBox>
      </Card.Section>

      <Card.Section className={cls.submitBtns}>
        <Button
          className={cls.submitBtn}
          color="primary"
          variation="accented"
          size="extra-large"
          fullWidth
          onClick={setPreviousStep}
          disabled={isLoading}
        >
          <Icon name="short-arrow-tiny-left" />
          Wstecz
        </Button>

        <Button
          className={cls.submitBtn}
          color="success"
          size="extra-large"
          fullWidth
          onClick={createManualOrder}
          disabled={isLoading}
        >
          Utwórz zlecenie
          <Icon name="short-arrow-tiny-right" />
        </Button>
      </Card.Section>
    </Card>
  )
}

const SummaryBox = ({ children }: { children: ReactNode }) => {
  return <div className={cls.summaryBox}>{children}</div>
}

const SummaryHeader = ({ iconName, children }: { iconName: IconName; children: ReactNode }) => {
  return (
    <div className={cls.summaryHeader}>
      <Icon name={iconName} gutter="right" color="primary" size="large" />
      <span>{children}</span>
    </div>
  )
}

const SummaryInfo = ({ children }: { children: ReactNode }) => {
  return <ul className={cls.summaryInfo}>{children}</ul>
}

const OrderCreated = ({ orderId }: { orderId: Order["id"] }) => {
  const { resetForm } = useManualOrderFormContext()

  const dispatch = useAppDispatch()
  const history = useHistory()

  return (
    <Card className={cls.card}>
      <Card.Section className={cls.created}>
        <Icon name="check-circle" color="success" size="extra-large" className={cls.icon} />
        <div className="h500">Zlecenie zostało utworzone poprawnie</div>
      </Card.Section>

      <Card.Section className={cls.submitBtns}>
        <Button
          className={cls.submitBtn}
          color="primary"
          variation="accented"
          size="extra-large"
          fullWidth
          onClick={resetForm}
        >
          <Icon name="check-circle" gutter="right" />
          Utwórz zlecenie
        </Button>

        <Button
          className={cls.submitBtn}
          color="success"
          size="extra-large"
          fullWidth
          onClick={() => {
            dispatch(setAboutPanel({ type: "order", id: orderId }))
            history.push("/")
          }}
        >
          Przejdź do zlecenia
          <Icon name="short-arrow-tiny-right" gutter="left" />
        </Button>
      </Card.Section>
    </Card>
  )
}

function humanizeAddress(data: Partial<ContactDetail>) {
  const flatNumber = data.flat_number ? ` / ${data.flat_number}` : ""

  return `${data.street} ${data.house_number}${flatNumber}, ${data.postal_code} ${data.city}`
}
